import { CanAccess, EditBase, Form, required } from "ra-core";
import { Navigate } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { BooleanInput } from "@/components/admin/boolean-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";

import { FormToolbar } from "../layout/FormToolbar";
import type { Developer } from "../types";
import { COMPLIANCE_PACK_STATUS_CHOICES } from "./complianceStatus";
import { ProjectInvoicingSettings } from "./ProjectInvoicingSettings";

const developerOptionRenderer = (choice: Developer) => choice.legal_name;

/**
 * ASSUMPTION: `code` and `invoice_concept_template_version` are read-only.
 * `code` feeds immutable invoicing operation IDs (ALM-{PROJ}-...), so it
 * must never change once assigned; `invoice_concept_template_version` is
 * bumped automatically by the database whenever the template changes
 * (TASK-004/TASK-002), never entered by hand.
 *
 * The whole page is also gated by `canAccess` so a non-direccion role can't
 * reach an editable form by typing the URL directly (react-admin's
 * <Resource edit=.../> route itself is not access-gated).
 */
export const ProjectEdit = () => (
  <CanAccess
    resource="projects"
    action="edit"
    accessDenied={<Navigate to="/projects" replace />}
  >
    <EditBase actions={false} redirect="list">
      <div className="mt-2 max-w-lg mx-auto space-y-4">
        <Card>
          <CardContent>
            <Form className="space-y-4 pt-4">
              <TextInput source="code" readOnly helperText={false} />
              <TextInput
                source="name"
                validate={required()}
                helperText={false}
              />
              <ReferenceInput source="developer_id" reference="developers">
                <SelectInput
                  optionText={developerOptionRenderer}
                  validate={required()}
                  helperText={false}
                />
              </ReferenceInput>
              <SelectInput
                source="compliance_pack_status"
                choices={COMPLIANCE_PACK_STATUS_CHOICES}
                validate={required()}
                helperText={false}
              />
              {/* Almikia Epic E6 (TASK-041): the template and its read-only
                  version are edited through their RPCs in the invoicing card
                  below, never through this generic form. */}
              {/* Almikia Epic E3b-2 ("Archive Mazarik"): direccion (the only
                  role that reaches this page, per the CanAccess gate above)
                  toggles a project archived/active. Hidden for new leads and
                  operations once archived, history kept, never deleted. */}
              <BooleanInput source="archived" helperText={false} />
              <FormToolbar />
            </Form>
          </CardContent>
        </Card>
        <ProjectInvoicingSettings />
      </div>
    </EditBase>
  </CanAccess>
);
